import * as React from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { NodeViewWrapper } from '@tiptap/react';

import { UploadOptions } from './types';

import ImageUploadPreview from './ImageUploadPreview';
import CloudUploadIcon from './CloudUploadIcon';

import 'containers/Tiptap/components/tiptap-node/image-upload-node/image-upload-node.scss';
import ImageUploadDragArea from './ImageUploadDragArea';
import useFileUploadTipTap from './useFileUpload';

// Context for file upload specific values
interface FileUploadContextType {
  entityId?: string;
  enqueueErrorSnackbar?: (message: string) => void;
}

export const FileUploadContext = React.createContext<FileUploadContextType>({});

export const FileUploadProvider: React.FC<{
  children: React.ReactNode;
  entityId?: string;
  enqueueErrorSnackbar?: (message: string) => void;
}> = ({ children, entityId, enqueueErrorSnackbar }) => (
  <FileUploadContext.Provider value={{ entityId, enqueueErrorSnackbar }}>{children}</FileUploadContext.Provider>
);

export const ImageUploadNode: React.FC<NodeViewProps> = ({
  node: {
    attrs: { limit, maxSize },
  },
  extension,
  getPos,
  editor,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const editorContext = React.useContext(FileUploadContext);

  const uploadOptions: UploadOptions = {
    editor,
    pos: getPos() ?? 0,
    maxSize,
    limit,
    upload: extension.options.upload,
    onSuccess: extension.options.onSuccess,
    onError: extension.options.onError,
    entityId: editorContext.entityId,
    enqueueErrorSnackbar: editorContext.enqueueErrorSnackbar,
  };

  const { fileItem, uploadFiles, clearFileItem } = useFileUploadTipTap(uploadOptions);

  if (!editor.isEditable) {
    return <NodeViewWrapper className="tiptap-image-upload" />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    if (!files || files.length === 0) {
      extension.options.onError?.(new Error('No file selected'));
      return;
    }
    handleUpload(Array.from(files));
  };

  const handleUpload = async (files: File[]): Promise<void> => {
    files.forEach(async (file) => {
      if (!file) return;

      const pos = getPos();
      if (pos === undefined) return;

      // For images, use the original upload logic
      if (file.type.startsWith('image/')) {
        const url = await uploadFiles(files);

        if (url) {
          const filename = file.name.replace(/\.[^/.]+$/, '') || 'unknown';

          editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + 1 })
            .insertContentAt(pos, [
              {
                type: 'image',
                attrs: { src: url, alt: filename, title: filename },
              },
              {
                type: 'paragraph',
              },
            ])
            .run();
        }
      } else if (uploadOptions.entityId && uploadOptions.enqueueErrorSnackbar) {
        editor
          .chain()
          .focus()
          .deleteRange({ from: pos, to: pos + 1 })
          .run();

        await uploadFiles(files);
      } else {
        // Fallback: show error if entityId or error handler not provided
        uploadOptions.onError?.(new Error('Entity ID or error handler not provided for attachment upload'));
      }
    });
  };

  const handleClick = (): void => {
    if (inputRef.current && !fileItem) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  return (
    <NodeViewWrapper className="tiptap-image-upload" tabIndex={0} onClick={handleClick}>
      {!fileItem && (
        <ImageUploadDragArea onFile={handleUpload}>
          <CloudUploadIcon />

          <div className="tiptap-image-upload-content">
            <span className="tiptap-image-upload-text">
              Drag and drop or <em>click to upload</em>
            </span>
            <span className="tiptap-image-upload-subtext">
              All file types supported. Maximum file size: {maxSize / 1024 / 1024}MB
            </span>
          </div>
        </ImageUploadDragArea>
      )}

      {fileItem && (
        <ImageUploadPreview
          file={fileItem.file}
          progress={fileItem.progress}
          status={fileItem.status}
          onRemove={clearFileItem}
        />
      )}

      <input
        ref={inputRef}
        name="file"
        type="file"
        multiple
        // don't pass accept prop to the input we want to allow all types
        onChange={handleChange}
        onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
      />
    </NodeViewWrapper>
  );
};
